/**
 * Service to handle character-related API calls
 */

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Create a new character
 * @param {String} token - Firebase Auth ID token
 * @param {Object} characterData - Character data (name, age, etc.)
 * @returns {Promise<object>} - Created character object
 */
export const createCharacter = async (token, characterData) => {
  const response = await fetch(`${API_URL}/characters`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(characterData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create character');
  }

  return data.data;
};

/**
 * Get all characters for the current user
 * @param {String} token - Firebase Auth ID token
 * @returns {Promise<Array>} - Array of character objects
 */
export const getCharacters = async (token) => {
  const response = await fetch(`${API_URL}/characters`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch characters');
  }

  return data.data;
};

/**
 * Update a character
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - ID of the character to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<object>} - Updated character object
 */
export const updateCharacter = async (token, characterId, updates) => {
  const response = await fetch(`${API_URL}/characters/${characterId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update character');
  }

  return data.data;
};

/**
 * Delete a character
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - ID of the character to delete
 * @returns {Promise<object>} - Success message
 */
export const deleteCharacter = async (token, characterId) => {
  const response = await fetch(`${API_URL}/characters/${characterId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete character');
  }

  return data;
};

/**
 * Generate and save a backstory for a character via AI
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - ID of the character
 * @returns {Promise<{backstory: string}>} - Generated backstory
 */
export const generateBackstory = async (token, characterId) => {
  const response = await fetch(`${API_URL}/characters/${characterId}/backstory`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to generate backstory');
  }

  return data.data;
};

// ===== ADMIN PREDEFINED CHARACTERS API FUNCTIONS =====

/**
 * List all predefined characters (admin only)
 * @param {String} token - Firebase Auth ID token
 * @param {Object} options - Pagination options { limit, startAfter }
 * @returns {Promise<{characters: Array, hasMore: boolean}>} - Paginated character list
 */
export const listPredefinedCharacters = async (token, options = {}) => {
  const { limit = 10, startAfter = null } = options;
  const params = new URLSearchParams({ limit });
  if (startAfter) params.append('startAfter', startAfter);

  console.log('[characterService.listPredefinedCharacters] Fetching from:', `${API_URL}/admin/predefinedCharacters?${params}`);
  const response = await fetch(`${API_URL}/admin/predefinedCharacters?${params}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();
  console.log('[characterService.listPredefinedCharacters] Full response:', data);
  console.log('[characterService.listPredefinedCharacters] Response status:', response.ok);
  console.log('[characterService.listPredefinedCharacters] Returning data.data:', data.data);

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch predefined characters');
  }

  return data.data;
};

/**
 * Get a single predefined character (admin only)
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - Character ID
 * @returns {Promise<object>} - Character object
 */
export const getPredefinedCharacter = async (token, characterId) => {
  const response = await fetch(`${API_URL}/admin/predefinedCharacters/${characterId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch character');
  }

  return data.data;
};

/**
 * Create a new predefined character (admin only)
 * @param {String} token - Firebase Auth ID token
 * @param {Object} characterData - Character data
 * @returns {Promise<object>} - Created character object
 */
export const createPredefinedCharacter = async (token, characterData) => {
  const response = await fetch(`${API_URL}/admin/predefinedCharacters`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(characterData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create predefined character');
  }

  return data.data;
};

/**
 * Update a predefined character (admin only)
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - Character ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<object>} - Updated character object
 */
export const updatePredefinedCharacter = async (token, characterId, updates) => {
  const response = await fetch(`${API_URL}/admin/predefinedCharacters/${characterId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update predefined character');
  }

  return data.data;
};

/**
 * Delete a predefined character (admin only) - soft delete
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - Character ID
 * @returns {Promise<object>} - Success message
 */
export const deletePredefinedCharacter = async (token, characterId) => {
  const response = await fetch(`${API_URL}/admin/predefinedCharacters/${characterId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete predefined character');
  }

  return data;
};

/**
 * Track character interaction (called when user starts a new chat)
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - Character ID
 * @returns {Promise<object>} - Success response
 */
export const trackCharacterInteraction = async (token, characterId) => {
  const response = await fetch(`${API_URL}/characters/${characterId}/interact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to track interaction');
  }

  return data;
};

/**
 * Get all characters for the character browser (custom + predefined)
 * @param {String} token - Firebase Auth ID token
 * @returns {Promise<object>} - Object with custom and predefined arrays
 */
export const listUserCharacters = async (token) => {
  const response = await fetch(`${API_URL}/characters/browser/all`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch characters');
  }

  return data;
};

/**
 * Get sidebar characters (top 10 for left panel)
 * @param {String} token - Firebase Auth ID token
 * @returns {Promise<Array>} - Array of max 10 characters
 */
export const getSidebarCharacters = async (token) => {
  const response = await fetch(`${API_URL}/characters/browser/sidebar`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch sidebar characters');
  }

  return data.data;
};
